import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { Column, RowRenderProps, SortingRule } from 'react-table';
import {
  useHasPermissionForTeamProjects,
  useHasPermissionForTeamReleaseGroups,
} from '../../../../helpers/permissions';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import { useDialogContext } from '../../../../contexts/DialogContext';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import { icons } from '../../../generic';
import {
  useOrganization,
  useAddProjectsToTeam,
  useAddReleaseGroupsToTeam,
  useRemoveProjectsFromTeam,
  useRemoveReleaseGroupsFromTeam,
} from '../../../../hooks/api';
import { Action } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import ModalAddTeamProjects from '../../../modals/ModalAddTeamProjects';
import type { Team } from '../../../../types';
import { useDispatchOnMount } from '../../../../hooks';
import ProjectGroupModule from '../../../../redux/project_grouping';

const defaultSortMode: SortingRule[] = [{ id: 'title', desc: false }];

//  Instead of moving this to React Query, I'm just going to use the existing Redux store for now.
function useReleaseGroups() {
  useDispatchOnMount(ProjectGroupModule.actions.fetchAll, {});
  const releaseGroups = useSelector(ProjectGroupModule.selectors.getEntities);
  const status = useSelector(ProjectGroupModule.selectors.getStatus);
  return {
    releaseGroups: useMemo(() => Object.values(releaseGroups), [releaseGroups]),
    isLoading: status.isLoading,
  };
}

type Props = {
  team: Team;
  search: string;
  isShowingAddProjectsModal: boolean;
  onHideAddProjectsModal: () => void;
};

type TeamDataRow =
  | {
      projectId: string;
      title: string;
      type: 'project';
    }
  | {
      releaseGroupId: number;
      title: string;
      type: 'releaseGroup';
    };

const prepareProjectData = (
  teamProjects: Team['teamProjects'],
  projects: Array<{ locator: string; title: string }>,
  teamReleaseGroups: Team['teamReleaseGroups'],
  releaseGroups: Array<{ id: number; title: string }>,
  search: string,
  sortMode: SortingRule,
): TeamDataRow[] => {
  const projectTitlesMap = new Map<string, string>(projects.map((project) => [project.locator, project.title]));
  const releaseGroupTitlesMap = new Map<number, string>(
    releaseGroups.map((releaseGroup) => [releaseGroup.id, releaseGroup.title]));

  const projectRows =
    teamProjects?.map(
      ({ projectId }): TeamDataRow => ({
        projectId,
        title: projectTitlesMap.get(projectId) || 'Loading...',
        type: 'project',
      }),
    ) || [];

  let releaseGroupRows: TeamDataRow[] = [];
  if (teamReleaseGroups && releaseGroups) {
    releaseGroupRows = teamReleaseGroups.map(
      ({ releaseGroupId }): TeamDataRow => ({
        releaseGroupId,
        title: releaseGroupTitlesMap.get(releaseGroupId) || 'Loading...',
        type: 'releaseGroup',
      }),
    );
  }

  let rows = [...projectRows, ...releaseGroupRows];

  if (search) {
    const searchLower = search.toLowerCase();
    rows = rows.filter((row) => row.title.toLowerCase().includes(searchLower));
  }

  const { id, desc: isDescending } = sortMode;
  const sortKey = id as keyof TeamDataRow;
  return rows.sort((a: TeamDataRow, b: TeamDataRow) => {
    if (isDescending) {
      return b[sortKey].toLowerCase().localeCompare(a[sortKey].toLowerCase());
    }
    return a[sortKey].toLowerCase().localeCompare(b[sortKey].toLowerCase());
  });
};

/**
 * Table of all of the projects and release groups for a team.
 *
 * @param {Props} props - The props for the component.
 * @param {Team} props.team - The team to display the projects and release groups for.
 * @param {string} props.search - The search string to filter the projects and release groups by.
 * @param {Array<{ id: number; title: string }>} props.releaseGroups - The release groups to display.
 * @param {boolean} props.isShowingAddProjectsModal - Whether the add projects modal is showing.
 * @param {() => void} props.onHideAddProjectsModal - The function to call when the add projects modal is hidden.
 */
const TeamProjects: VoidFunctionComponent<Props> = ({
  team,
  search,
  isShowingAddProjectsModal,
  onHideAddProjectsModal,
}) => {
  const { onConfirm } = useDialogContext();
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'title', desc: false });

  //  Queries
  //  Note: We pull the projects from the organization because there is no (simple) way to get all the projects for an organization.
  const { data: organization, isLoading: isLoadingOrganization } = useOrganization(
    window.user?.organizationId as number);
  const { releaseGroups, isLoading: isLoadingReleaseGroups } = useReleaseGroups();
  //  Mutations
  const { mutateAsync: addProjectsToTeam, isLoading: isAddingProjects } = useAddProjectsToTeam();
  const { mutateAsync: addReleaseGroupsToTeam, isLoading: isAddingReleaseGroups } = useAddReleaseGroupsToTeam();
  const { mutateAsync: removeProjectsFromTeam, isLoading: isRemovingProjects } = useRemoveProjectsFromTeam();
  const {
    mutateAsync: removeReleaseGroupsFromTeam,
    isLoading: isRemovingReleaseGroups,
  } = useRemoveReleaseGroupsFromTeam();

  const projectData = useMemo(() => prepareProjectData(
    team.teamProjects,
    organization?.projects || [],
    team.teamReleaseGroups,
    releaseGroups,
    search,
    sortMode,
  ), [team, organization?.projects, search, sortMode, releaseGroups]);
  //  Permissions
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const canRemoveProjects = hasPermissionForTeamProjects(Action.Remove, team.id);
  const hasPermissionForTeamReleaseGroups = useHasPermissionForTeamReleaseGroups();
  const canRemoveReleaseGroups = hasPermissionForTeamReleaseGroups(Action.Remove, team.id);

  //  Returns the click handler for removing projects/releaseGroups. Displays confirm modal first.
  const onRemoveClick = useCallback(
    ({ isProject, id, title }: { isProject: boolean; id: string | number; title: string }) =>
      () =>
        onConfirm({
          title: `Are you sure you want to remove "${title}"?`,
          body: `These ${isProject ? 'project(s)' : 'release group(s)'} will no longer be available to this team.`,
          onConfirm: () => isProject
            ? removeProjectsFromTeam({ teamId: team.id, projectIds: [id as string] })
            : removeReleaseGroupsFromTeam({ teamId: team.id, releaseGroupIds: [id as number] }),
          confirmText: 'Remove',
          confirmBtnType: 'error',
        }),
    [onConfirm, removeProjectsFromTeam, removeReleaseGroupsFromTeam, team.id],
  );

  const columns = useMemo<Array<Column<TeamDataRow>>>(() => [
    {
      id: 'title',
      accessor: 'title',
      Header: <TableHeader title="Name" sort={{ id: 'title', currentMode: sortMode }} />,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: TeamDataRow } & RowRenderProps) => <span>{original.title}</span>,
      resizable: false,
    },
    {
      id: 'type',
      accessor: 'type',
      Header: <TableHeader title="Type" sort={{ id: 'type', currentMode: sortMode }} />,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: TeamDataRow }) => (
        <span>
          {original.type === 'project' ? 'Project' : 'Release Group'}
        </span>
      ),
      resizable: false,
      width: 250,
    },
    {
      id: 'actions',
      accessor: 'actions',
      Header: <TableHeader title="Actions" />,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: { original: TeamDataRow }) => original.type === 'releaseGroup'
        ? (
          <SettingsTableRowActions>
            <SettingsTableRowActions.IconButton
              tooltipId={`remove-release-group-btn-${original.releaseGroupId}`}
              tooltip={!canRemoveReleaseGroups ? 'You do not have permission to remove release groups from this team.' : 'Remove Release Group'}
              icon={icons.trashRegular}
              isDisabled={!canRemoveReleaseGroups}
              onClick={onRemoveClick({
                title: original.title,
                id: original.releaseGroupId,
                isProject: false,
              })}
            />
          </SettingsTableRowActions>
          )
        : (
          <SettingsTableRowActions>
            <SettingsTableRowActions.IconButton
              tooltipId={`remove-project-btn-${original.projectId}`}
              tooltip={!canRemoveProjects ? 'You do not have permission to remove projects from this team.' : 'Remove Project'}
              icon={icons.trashRegular}
              isDisabled={!canRemoveProjects}
              onClick={onRemoveClick({
                title: original.title,
                id: original.projectId,
                isProject: true,
              })}
          />
          </SettingsTableRowActions>
          ),
      width: 70,
      resizable: false,
      sortable: false,
    },
  ], [sortMode, canRemoveReleaseGroups, canRemoveProjects, onRemoveClick]);

  return (
    <>
      <SimpleReactTable
        data={projectData}
        columns={columns}
        defaultSortMode={defaultSortMode}
        onSortedChanged={setSortMode}
        showPagination={projectData.length > 20}
        cacheId="teamProjects"
        isLoading={
          isLoadingOrganization ||
          isLoadingReleaseGroups ||
          isAddingProjects ||
          isAddingReleaseGroups ||
          isRemovingProjects ||
          isRemovingReleaseGroups
        }
      />
      {isShowingAddProjectsModal && (
        <ModalAddTeamProjects
          team={team}
          projects={organization?.projects || []}
          releaseGroups={releaseGroups}
          onDone={async (projectsIds: string[], releaseGroupIds: number[]) => {
            //  Note: these queries need to be done sequentially, as the release group can only be added after the projects are added.
            if (projectsIds.length > 0) {
              await addProjectsToTeam({ teamId: team.id, projectIds: projectsIds });
            }

            if (releaseGroupIds.length > 0) {
              await addReleaseGroupsToTeam({ teamId: team.id, releaseGroupIds });
            }

            onHideAddProjectsModal();
          }}
          onHide={onHideAddProjectsModal}
        />
      )}
    </>
  );
};

export default TeamProjects;
